/**
 * 客户端图片优化插件
 * 用于实现图片的惰性加载和优先级设置
 */
export default defineNuxtPlugin((nuxtApp) => {
  // 仅在客户端执行
  if (process.client) {
    nuxtApp.hook('app:mounted', () => {
      // 添加交叉观察器，用于惰性加载非关键图片
      const lazyLoadImages = () => {
        if ('IntersectionObserver' in window) {
          const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const lazyImage = entry.target as HTMLImageElement;
                if (lazyImage.dataset.src) {
                  lazyImage.src = lazyImage.dataset.src;
                  lazyImage.removeAttribute('data-src');
                  lazyImage.classList.remove('lazy-load');
                  imageObserver.unobserve(lazyImage);
                }
              }
            });
          }, {
            rootMargin: '200px 0px', // 提前200px开始加载
            threshold: 0.01
          });
          
          // 选择所有带有lazy-load类的图片
          const lazyImages = document.querySelectorAll('img.lazy-load');
          lazyImages.forEach(img => {
            imageObserver.observe(img);
          });
        } else {
          // 对于不支持IntersectionObserver的浏览器，降级处理
          const lazyImages = Array.from(document.querySelectorAll('img.lazy-load'));
          const loadImages = () => {
            if (lazyImages.length === 0) {
              document.removeEventListener('scroll', lazyImageLoader);
              (window as any).removeEventListener('resize', lazyImageLoader);
              (window as any).removeEventListener('orientationchange', lazyImageLoader);
              return;
            }
            
            lazyImages.forEach((lazyImage, index) => {
              const imgElement = lazyImage as HTMLImageElement;
              if (imgElement.getBoundingClientRect().top <= window.innerHeight + 200) {
                if (imgElement.dataset.src) {
                  imgElement.src = imgElement.dataset.src;
                  imgElement.removeAttribute('data-src');
                  imgElement.classList.remove('lazy-load');
                  lazyImages.splice(index, 1);
                }
              }
            });
          };
          
          const lazyImageLoader = () => {
            requestAnimationFrame(loadImages);
          };
          
          // 添加滚动、调整大小和旋转事件
          document.addEventListener('scroll', lazyImageLoader);
          (window as any).addEventListener('resize', lazyImageLoader);
          (window as any).addEventListener('orientationchange', lazyImageLoader);
          
          // 初始加载
          lazyImageLoader();
        }
      };
      
      // 设置图片尺寸以避免布局偏移
      const setImageDimensions = () => {
        const images = document.querySelectorAll('img:not([width]):not([height])');
        images.forEach(img => {
          const imgElement = img as HTMLImageElement;
          if (imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0) {
            imgElement.width = imgElement.naturalWidth;
            imgElement.height = imgElement.naturalHeight;
          }
        });
      };

      // 初始加载时执行
      lazyLoadImages();
      setImageDimensions();
      
      // 为动态加载的内容添加图片惰性加载支持
      nuxtApp.hook('page:finish', () => {
        setTimeout(() => {
          lazyLoadImages();
          setImageDimensions();
        }, 200);
      });
    });
  }
}); 